<template>
  <b-overlay :show="loading" :opacity="0.6">
    <!-- Spouse Link Modal -->
    <b-modal
      v-model="showSpouseLinkModal"
      :title="spouse && spouse._id ? 'Update Spouse' : 'Link Spouse'"
      size="lg"
      @ok="saveSpouseLink"
      @hidden="resetSpouseSearch">
      <b-form-group
        label="Search for Spouse"
        label-for="spouse-search"
        description="Start typing to search for a member profile to link as spouse">
        <v-select
          id="spouse-search"
          v-model="selectedSpouse"
          :options="spouseSearchResults"
          :loading="searchingSpouse"
          :clearable="true"
          placeholder="Search by name or email..."
          @search="onSpouseSearch"
          label="label">
          <template #option="option">
            <div class="d-flex align-items-center">
              <b-avatar 
                size="32" 
                :src="option.avatar_path"
                :text="avatarText(option.label)"
                variant="light-primary"
                class="mr-2"
              />
              <div>
                <div class="font-weight-semibold">{{ option.label }}</div>
                <small class="text-muted">{{ option.email }}</small>
              </div>
            </div>
          </template>
          <template #selected-option="option">
            <div class="d-flex align-items-center">
              <b-avatar 
                size="24" 
                :src="option.avatar_path"
                :text="avatarText(option.label)"
                variant="light-primary"
                class="mr-2"
              />
              <span>{{ option.label }}</span>
            </div>
          </template>
        </v-select>
      </b-form-group>
      
      <div v-if="selectedSpouse" class="selected-spouse-preview mt-3 p-3 border rounded">
        <h6>Selected Profile:</h6>
        <div class="d-flex align-items-center">
          <b-avatar 
            size="48" 
            :src="selectedSpouse.avatar_path"
            :text="avatarText(selectedSpouse.label)"
            variant="light-primary"
            class="mr-3"
          />
          <div>
            <div class="font-weight-semibold">{{ selectedSpouse.label }}</div>
            <div class="text-muted">{{ selectedSpouse.email }}</div>
            <div class="text-muted">{{ selectedSpouse.location }}</div>
          </div>
        </div>
      </div>
    </b-modal>
    
    <div v-if="minister && memberProfile" id="profile-view" class="minister-profile-enhanced">
      <!-- Hero Header Section -->
      <div class="hero-header">
        <div class="hero-pattern"></div>
        <div class="container-fluid">
          <b-row class="align-items-center py-4">
            <b-col cols="12" md="8" class="text-center text-md-left">
              <div class="d-flex flex-column flex-md-row align-items-center">
                <div class="avatar-wrapper mb-3 mb-md-0 mr-md-4">
                  <b-avatar
                    ref="previewEl"
                    :src="avatarPath"
                    :text="avatarText(`${minister.first_name} ${minister.last_name}`)"
                    variant="white"
                    size="120"
                    class="minister-avatar"
                  />
                  <div class="avatar-badge">
                    <feather-icon icon="CheckCircleIcon" size="24" />
                  </div>
                </div>
                <div class="minister-header-info">
                  <h1 class="minister-name mb-2">{{ minister.full_name }}</h1>
                  <p class="minister-title mb-2">{{ memberProfile.personalInformation.Title }}, {{ memberProfile.personalInformation.Designation }}</p>
                  <div class="minister-meta">
                    <span class="meta-item">
                      <feather-icon icon="MapPinIcon" size="14" />
                      {{ memberProfile.location.District }}
                    </span>
                    <span class="meta-divider">•</span>
                    <span class="meta-item">
                      <feather-icon icon="CalendarIcon" size="14" />
                      Serving {{ yearsServedAtCurrentStation }} years
                    </span>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="12" md="4" class="text-center text-md-right mt-3 mt-md-0">
              <b-button variant="light" class="action-btn mr-2" @click="$router.push(editRoute)">
                <feather-icon icon="EditIcon" size="16" />
                <span>Edit Profile</span>
              </b-button>
              <b-dropdown variant="primary" class="action-btn">
                <template #button-content>
                  <feather-icon icon="DownloadIcon" size="16" />
                  <span>Download</span>
                </template>
                <b-dropdown-item @click="download('pdf')">
                  <feather-icon icon="FileTextIcon" size="14" />
                  Download as PDF
                </b-dropdown-item>
                <b-dropdown-item @click="download('xlsx')">
                  <feather-icon icon="FileIcon" size="14" />
                  Download as Excel
                </b-dropdown-item>
              </b-dropdown>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="container-fluid mt-4 pb-5">
        <b-row>
          <!-- Sidebar Info Card -->
          <b-col cols="12" lg="3" class="mb-4">
            <div class="info-card">
              <h5 class="card-header-title mb-4">
                <feather-icon icon="UserIcon" size="18" />
                Personal Information
              </h5>
              
              <div class="info-item">
                <span class="info-label">Date of Birth</span>
                <span class="info-value">{{ memberProfile.personalInformation['Date of Birth'] }}</span>
              </div>
              
              <div class="info-item">
                <span class="info-label">Gender</span>
                <span class="info-value">{{ memberProfile.personalInformation.Gender }}</span>
              </div>
              
              <div class="info-item">
                <span class="info-label">Phone</span>
                <span class="info-value">
                  <a :href="`tel:${memberProfile.personalInformation.Phone}`" class="contact-link">
                    {{ memberProfile.personalInformation.Phone }}
                  </a>
                </span>
              </div>
              
              <div class="info-item">
                <span class="info-label">Email</span>
                <span class="info-value">
                  <a :href="`mailto:${memberProfile.personalInformation.Email}`" class="contact-link">
                    {{ memberProfile.personalInformation.Email || '-' }}
                  </a>
                </span>
              </div>
            </div>

            <div class="info-card mt-4">
              <h5 class="card-header-title mb-4">
                <feather-icon icon="MapIcon" size="18" />
                Location Details
              </h5>
              
              <div v-for="(locationEntry, index) of Object.entries(memberProfile.location)" 
                   :key="index" 
                   class="info-item">
                <span class="info-label">{{ locationEntry[0] }}</span>
                <span class="info-value">{{ locationEntry[1] }}</span>
              </div>
            </div>
          </b-col>

          <!-- Main Content Tabs -->
          <b-col cols="12" lg="9">
            <b-tabs class="minister-tabs" pills>
              <b-tab v-if="ministersOnly" active>
                <template #title>
                  <feather-icon icon="BookOpenIcon" size="16" />
                  <span>Ministry</span>
                </template>
                <div class="tab-card">
                  <div class="tab-header">
                    <h4 class="tab-title">Ministry Information</h4>
                  </div>
                  <div class="tab-content">
                    <div class="ministry-grid">
                      <div v-for="(value, key) in memberProfile.ministry" 
                           :key="key" 
                           class="ministry-item"
                           :class="{ 'full-width': key === 'Stations Served' }">
                        <div class="ministry-label">{{ key }}</div>
                        <div class="ministry-value">{{ value }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>
              
              <b-tab v-if="ministersOnly">
                <template #title>
                  <feather-icon icon="UsersIcon" size="16" />
                  <span>Spouse</span>
                </template>
                <div class="tab-card">
                  <div class="tab-header">
                    <h4 class="tab-title">Spouse Information</h4>
                    <b-button
                      v-if="spouse && spouse._id"
                      variant="outline-light"
                      size="sm"
                      @click="showSpouseLinkModal = true">
                      <feather-icon icon="EditIcon" size="14" />
                      Update Spouse
                    </b-button>
                  </div>
                  <div class="tab-content">
                    <!-- Spouse exists -->
                    <div v-if="spouse && spouse._id" class="spouse-card">
                      <div class="spouse-header">
                        <b-avatar 
                          size="80" 
                          variant="light-primary"
                          :text="avatarText(memberProfile.wife.Name)"
                        />
                        <div class="spouse-info">
                          <h5 class="spouse-name">{{ memberProfile.wife.Name }}</h5>
                          <p class="spouse-profession">{{ memberProfile.wife.Profession }}</p>
                        </div>
                      </div>
                      <div class="ministry-grid mt-4">
                        <template v-for="(value, key) in memberProfile.wife">
                          <div v-if="key !== 'Name' && key !== 'Profession'"
                               :key="key" 
                               class="ministry-item">
                            <div class="ministry-label">{{ key }}</div>
                            <div class="ministry-value">{{ value }}</div>
                          </div>
                        </template>
                      </div>
                      <div class="d-flex gap-2 mt-4">
                        <b-button 
                          variant="primary"
                          :to="spouseDetailLink">
                          <feather-icon icon="ExternalLinkIcon" size="16" />
                          View Full Profile
                        </b-button>
                        <b-button 
                          variant="outline-danger"
                          @click="unlinkSpouse">
                          <feather-icon icon="UserXIcon" size="16" />
                          Unlink Spouse
                        </b-button>
                      </div>
                    </div>
                    
                    <!-- No spouse -->
                    <div v-else class="no-spouse-container">
                      <div class="no-spouse-illustration">
                        <feather-icon icon="UsersIcon" size="64" />
                      </div>
                      <h5 class="no-spouse-title">No Spouse Information</h5>
                      <p class="no-spouse-description">
                        Spouse information has not been added to this profile yet.
                      </p>
                      <b-button 
                        variant="primary"
                        @click="showSpouseLinkModal = true">
                        <feather-icon icon="UserPlusIcon" size="16" />
                        Link Spouse
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-tab>
              
              <b-tab>
                <template #title>
                  <feather-icon icon="AwardIcon" size="16" />
                  <span>Education</span>
                </template>
                <div class="tab-card">
                  <div class="tab-header">
                    <h4 class="tab-title">Educational History</h4>
                  </div>
                  <div class="tab-content">
                    <EducationHistoryList 
                      v-if="minister.user_id" 
                      :user-id="minister.user_id" 
                      :is-minister="ministersOnly" />
                  </div>
                </div>
              </b-tab>
              
              <b-tab>
                <template #title>
                  <feather-icon icon="BriefcaseIcon" size="16" />
                  <span>Work</span>
                </template>
                <div class="tab-card">
                  <div class="tab-header">
                    <h4 class="tab-title">Work Experience</h4>
                  </div>
                  <div class="tab-content">
                    <WorkExperienceList 
                      v-if="minister.user_id" 
                      :user-id="minister.user_id" />
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import MemberProfileSection from "@/@core/components/member-profile-section/member-profile-section.vue";
import EducationHistoryList from "@/@core/components/education/EducationHistoryList.vue";
import WorkExperienceList from "@/@core/components/work-experience/WorkExperienceList.vue";

import {
  BLink,
  BButton,
  BModal,
  BRow,
  BCol,
  BTooltip,
  BAvatar,
  BMedia,
  BBadge,
  BDropdown,
  BDropdownItem,
  BOverlay,
  BTabs,
  BTab,
  BCard,
  BListGroup,
  BListGroupItem,
  BFormGroup,
} from "bootstrap-vue";
import { get, startCase, debounce } from "lodash";

export default {
  components: {
    BRow,
    BCol,
    BTab,
    BTabs,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    BOverlay,
    ListTable,
    BDropdown,
    BCard,
    BListGroup,
    BListGroupItem,
    BDropdownItem,
    BFormGroup,
    WorkExperienceList,
    EducationHistoryList,
    MemberProfileSection,
  },
  directives: {
    Ripple,
  },
  props: {
    ministersOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      minister: null,

      search: "",
      updateData: null,
      isAddOrUpdateActive: false,
      
      // Spouse linking
      showSpouseLinkModal: false,
      selectedSpouse: null,
      spouseSearchResults: [],
      searchingSpouse: false,
      debouncedSpouseSearch: null,
    };
  },
  computed: {
    editRoute() {
      if (!this.minister || !this.minister._id) return {};
      const name = this.ministersOnly ? "ministers" : "members"
      const route = { name: `${name}-detail`, params: { id: this.minister._id } }
      return route
    },
    avatarPath() {
      return get(this.minister, "avatar.path", null);
    },
    spouse() {
      const wife = this.getValueFromSource(this.minister, "wife_data", null);
      const husband = this.getValueFromSource(this.minister, "husband_data", null);
      return wife || husband || null;
    },
    spouseDetailLink() {
      console.log("spouse profile", this.spouse)
      return { name: 'members-detail', params: { id: get(this.spouse, '_id') } }
    },
    memberProfile() {
      if (!this.minister) return null;
      return {
        personalInformation: {
          Name: this.minister.full_name,
          Email: this.getActualValueFromSource(this.minister, "user.email"),
          Phone: this.getActualValueFromSource(this.minister, "user.phone", ' - '),
          "Date of Birth": this.formatDate(this.minister.date_of_birth, "do MMM yyyy"),
          Gender: startCase(this.minister.gender),
          Title: this.getActualValueFromSource(this.minister, "member_title.title", ' - '),
          Designation: this.getActualValueFromSource(this.minister, "member_designation.designation", ' - '),
        },
        location: this.getLocationData(),
        ministry: {
          "Minister ID": this.getActualValueFromSource(this.minister, "minister_id", ' - '),
          "Years Called Into Ministry": this.getActualValueFromSource(this.minister, "year_called_into_ministry", ' - '),
          "Date of Engagement": this.minister.date_of_engagement ? this.formatDate(this.minister.date_of_engagement, "do MMMM yyyy") : ' - ',
          "Years in Current Area": this.getActualValueFromSource(this.minister, "years_in_current_area", ' - '),
          "Years in Current Station": this.yearsServedAtCurrentStation,
          "Called From": this.getActualValueFromSource(this.minister, "called_from", ' - '),
          "Languages Spoken": this.getActualValueFromSource(this.minister, "languages_spoken", ' - '),
          Qualifications: this.getActualValueFromSource(this.minister, "qualifications", ' - '),
          Profession: this.getActualValueFromSource(this.minister, "profession", ' - '),
          "Stations Served": this.stationsServed,
        },
        wife: {
          Name: this.getValueFromSource(this.spouse, 'full_name', ' - '),
          Email: this.getValueFromSource(this.spouse, 'user.email', ' - '),
          Phone: this.getValueFromSource(this.spouse, 'user.phone', ' - '),
          Hometown: this.getValueFromSource(this.spouse, 'home_town', ' - '),
          Profession: this.getValueFromSource(this.spouse, 'profession', ' - '),
        }
      }
    },
    getStationsServedList() {
      if (!this.minister) return [];
      const oldStationsServedData = this.getActualValueFromSource(this.minister, "stations", [])
      const newStationsServedData = this.minister.stations_served.map((station) => ({
        title: this.getStationTitle(station),
        year: station.year,
      }));
      const all_data = [...newStationsServedData, ...oldStationsServedData]
      all_data.sort((a, b) => Number(b.year) - Number(a.year));
      return all_data
    },
    stationsServed() {
      return this.getStationsServedList.map((item) => `${item.title} (${item.year})`).join(", ");
    },
    yearsServedAtCurrentStation() {
      if (!this.minister) return 0;
      if (this.getStationsServedList.length === 0) return 0;
      const currentStation = this.getStationsServedList[0];
      const currentYear = new Date().getFullYear();
      return currentYear - +currentStation.year;
    },
  },
  created() {
    this.fetchPageData();
    this.debouncedSpouseSearch = debounce(this.searchSpouse, 500);
  },
  methods: {
    async fetchPageData() {
      try {
        this.loading = true;

        const { id } = this.$route.params;

        const request = await this.useJwt().mainService.memberProfiles.get(id);
        const { data } = request.data;

        this.minister = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async download(format = "pdf") {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Download?',
          text: "This will download the member profile information as a PDF file.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, download.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }
        
        const response = await this.useJwt().mainService.memberProfiles.downloadMemberData(this.minister._id, { format });
        const filename = `Pentecost-${this.minister.full_name}.${format}`;

        this.downloadFile(response.data, filename);
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    makeDetail(source) {
      return Object.entries(source).map(([title, value]) => ({
        title,
        value: value || "-",
      }));
    },
    getLocationData() {
      const locationData = {
        Region: this.getGeoData(this.minister, "region"),
      };
      
      // Check if designation is Area Head using is_cop_area_head flag
      const isAreaHead = this.getActualValueFromSource(this.minister, "member_designation.is_cop_area_head", false);
      // Always add Area
      locationData.Area = this.getGeoData(this.minister, "area");
      
      // Only add District if not an Area Head (when they are a minister)
      if (!isAreaHead || !this.ministersOnly) {
        locationData.District = this.getGeoData(this.minister, "district");
      }
      
      // Only add Assembly if not a minister
      if (!this.ministersOnly) {
        locationData.Assembly = this.getGeoData(this.minister, "assembly");
      }
      
      locationData["Home Town"] = this.getActualValueFromSource(this.minister, "home_town", ' - ');
      
      return locationData;
    },
    
    // Spouse linking methods
    onSpouseSearch(search) {
      if (search && search.length > 2) {
        this.debouncedSpouseSearch(search);
      }
    },
    
    async searchSpouse(searchTerm) {
      try {
        this.searchingSpouse = true;
        
        // Search for member profiles excluding current minister
        const response = await this.useJwt().mainService.memberProfiles.list({
          search: searchTerm,
          limit: 10,
        });
        
        const { data } = response.data;
        
        // Format results for v-select and exclude current minister
        this.spouseSearchResults = data
          .filter(profile => profile._id !== this.minister._id)
          .map(profile => ({
            _id: profile._id,
            label: profile.full_name,
            email: get(profile, 'user.email', 'No email'),
            location: `${get(profile, 'district_data.title', '')} - ${get(profile, 'assembly_data.title', '')}`,
            avatar_path: get(profile, 'avatar.path', null),
          }));
      } catch (error) {
        console.error('Error searching for spouse:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Failed to search for spouse profiles'
          },
        });
      } finally {
        this.searchingSpouse = false;
      }
    },
    
    resetSpouseSearch() {
      this.selectedSpouse = null;
      this.spouseSearchResults = [];
    },
    
    async saveSpouseLink() {
      try {
        if (!this.selectedSpouse) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Warning!',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
              text: 'Please select a spouse profile'
            },
          });
          return;
        }
        
        this.loading = true;
        
        // Update the member profile with spouse ID
        await this.useJwt().mainService.memberProfiles.linkSpouse(
          this.minister._id,
          { spouse_id: this.selectedSpouse._id }
        );
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Spouse linked successfully'
          },
        });
        
        // Refresh the page data to show the updated spouse
        await this.fetchPageData();
        this.showSpouseLinkModal = false;
        this.resetSpouseSearch();
      } catch (error) {
        console.error('Error linking spouse:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: get(error, "response.data.message") || 'Failed to link spouse'
          },
        });
      } finally {
        this.loading = false;
      }
    },
    
    async unlinkSpouse() {
      try {
        const result = await this.$swal({
          title: 'Unlink Spouse?',
          text: "Are you sure you want to unlink the spouse from this profile?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, unlink',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        
        if (!result.value) return;
        
        this.loading = true;
        
        // Remove spouse link
        await this.useJwt().mainService.memberProfiles.linkSpouse(
          this.minister._id,
          { spouse_id: null }
        );
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Spouse unlinked successfully'
          },
        });
        
        // Refresh the page data
        await this.fetchPageData();
      } catch (error) {
        console.error('Error unlinking spouse:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: get(error, "response.data.message") || 'Failed to unlink spouse'
          },
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.minister-profile-enhanced {
  min-height: 100vh;
  background: #f8f9fa;

  // Hero Header Section
  .hero-header {
    position: relative;
    background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
    color: white;
    overflow: hidden;

    .hero-pattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 0.1;
      background-image: 
        repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px),
        repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px);
    }

    .container-fluid {
      position: relative;
      z-index: 1;
    }

    .avatar-wrapper {
      position: relative;
      display: inline-block;

      .minister-avatar {
        border: 4px solid rgba(255, 255, 255, 0.3);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }

      .avatar-badge {
        position: absolute;
        bottom: 5px;
        right: 5px;
        background: #10b981;
        border-radius: 50%;
        padding: 4px;
        border: 3px solid white;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .minister-header-info {
      text-align: left;

      @media (max-width: 767px) {
        text-align: center;
      }
    }

    .minister-name {
      font-size: 2rem;
      font-weight: 700;
      margin: 0;
      color: white;

      @media (max-width: 767px) {
        font-size: 1.5rem;
      }
    }

    .minister-title {
      font-size: 1.1rem;
      opacity: 0.95;
      font-weight: 500;
    }

    .minister-meta {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 0.9rem;
      opacity: 0.9;

      @media (max-width: 767px) {
        justify-content: center;
      }

      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .meta-divider {
        opacity: 0.5;
      }
    }

    .action-btn {
      padding: 10px 20px;
      font-weight: 500;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      margin: 4px;

      span {
        @media (max-width: 576px) {
          display: none;
        }
      }
    }
  }

  // Info Cards
  .info-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }

    .card-header-title {
      font-size: 1rem;
      font-weight: 600;
      color: #1e3a5f;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 2px solid #e5e7eb;
    }

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: start;
      padding: 12px 0;
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: none;
      }

      .info-label {
        font-size: 0.875rem;
        color: #6b7280;
        font-weight: 500;
        min-width: 80px;
      }

      .info-value {
        font-size: 0.875rem;
        color: #1f2937;
        font-weight: 600;
        text-align: right;
        flex: 1;
      }

      .contact-link {
        color: #2563eb;
        text-decoration: none;
        transition: color 0.2s;

        &:hover {
          color: #1d4ed8;
          text-decoration: underline;
        }
      }
    }
  }

  // Tabs Styling
  .minister-tabs {
    ::v-deep .nav-pills {
      border-bottom: none;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;

      @media (min-width: 768px) {
        justify-content: flex-start;
      }

      .nav-item {
        .nav-link {
          background: white;
          color: #4b5563;
          border-radius: 8px;
          padding: 12px 20px;
          font-weight: 500;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;

          &:hover {
            background: #f3f4f6;
            border-color: #d1d5db;
          }

          &.active {
            background: #1e3a5f;
            color: white;
            border-color: #1e3a5f;
            box-shadow: 0 4px 12px rgba(30, 58, 95, 0.2);
          }
        }
      }
    }

    ::v-deep .tab-content {
      margin-top: 24px;
    }
  }

  // Tab Cards
  .tab-card {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    .tab-header {
      background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
      color: white;
      padding: 20px 24px;

      .tab-title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: white;
      }
    }

    .tab-content {
      padding: 24px;
    }
  }

  // Ministry Grid
  .ministry-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;

    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }

    .ministry-item {
      padding: 16px;
      background: #f9fafb;
      border-radius: 8px;
      border: 1px solid #e5e7eb;

      &.full-width {
        grid-column: 1 / -1;
      }

      .ministry-label {
        font-size: 0.75rem;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
        font-weight: 600;
      }

      .ministry-value {
        font-size: 1rem;
        color: #1f2937;
        font-weight: 600;
        line-height: 1.5;
      }
    }
  }

  // Spouse Card
  .spouse-card {
    .spouse-header {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 20px;
      background: #f9fafb;
      border-radius: 12px;
      margin-bottom: 20px;

      @media (max-width: 576px) {
        flex-direction: column;
        text-align: center;
      }

      .spouse-info {
        flex: 1;

        .spouse-name {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .spouse-profession {
          margin: 4px 0 0;
          color: #6b7280;
          font-size: 1rem;
        }
      }
    }
  }
  
  // No Spouse Container
  .no-spouse-container {
    text-align: center;
    padding: 60px 20px;
    
    .no-spouse-illustration {
      margin-bottom: 24px;
      color: #d1d5db;
    }
    
    .no-spouse-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #374151;
      margin-bottom: 8px;
    }
    
    .no-spouse-description {
      color: #6b7280;
      margin-bottom: 24px;
      max-width: 400px;
      margin-left: auto;
      margin-right: auto;
    }
  }
  
  // Tab header modifications
  .tab-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    button {
      margin-left: auto;
    }
  }
  
  // Gap utility for button groups
  .gap-2 {
    gap: 0.5rem;
  }
}

// Dark mode support
.dark-layout {
  .minister-profile-enhanced {
    background: #1f2937;

    .info-card,
    .tab-card {
      background: #374151;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);

      .card-header-title {
        color: #e5e7eb;
        border-bottom-color: #4b5563;
      }

      .info-item {
        border-bottom-color: #4b5563;

        .info-label {
          color: #9ca3af;
        }

        .info-value {
          color: #f3f4f6;
        }
      }
    }

    .minister-tabs {
      ::v-deep .nav-pills .nav-item .nav-link {
        background: #374151;
        color: #d1d5db;
        border-color: #4b5563;

        &:hover {
          background: #4b5563;
        }

        &.active {
          background: #1e3a5f;
          color: white;
        }
      }
    }

    .ministry-grid .ministry-item {
      background: #2d3748;
      border-color: #4b5563;

      .ministry-label {
        color: #9ca3af;
      }

      .ministry-value {
        color: #f3f4f6;
      }
    }
  }
}
</style>
